import { useState, useCallback, useEffect, useRef } from "react";
import "@/App.css";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/site/Navbar";
import Hero from "@/components/site/Hero";
import HeroMarquee from "@/components/site/HeroMarquee";
import FoundingStats from "@/components/site/FoundingStats";
import QuietFrustration from "@/components/site/QuietFrustration";
import WhatSupercoreIs from "@/components/site/WhatSupercoreIs";
import WhatThisChanges from "@/components/site/WhatThisChanges";
import Clusters from "@/components/site/Clusters";
import SpecialistsMosaic from "@/components/site/SpecialistsMosaic";
import FirstNamed from "@/components/site/FirstNamed";
import MovementStatement from "@/components/site/MovementStatement";
import Footer from "@/components/site/Footer";
import PatientView from "@/components/site/PatientView";
import LoginPage from "@/components/site/LoginPage";
import CreateAccountPage from "@/components/site/CreateAccountPage";
import DoctorOnboardingPage from "@/components/site/DoctorOnboardingPage";
import PatientOnboardingPage from "@/components/site/PatientOnboardingPage";
import DoctorDashboard from "@/components/site/DoctorDashboard";
import AdminDashboard from "@/components/site/AdminDashboard";
import UserDashboard from "@/components/site/UserDashboard";
import ForgotPasswordPage from "@/components/site/ForgotPasswordPage";
import ResetPasswordPage from "@/components/site/ResetPasswordPage";

// Views that are safe to restore directly after a refresh (no extra API check needed)
const RESTORABLE_VIEWS = new Set([
  "doctor-dashboard",
  "admin-dashboard",
  "user-dashboard",
  "doctor-onboarding",
  "patient-onboarding",
]);

function App() {
  const { user, initialized } = useAuth();
  const [audience,    setAudience]    = useState("doctors");
  const [view,        setView]        = useState("");
  const [doctorName,  setDoctorName]  = useState("");
  const [patientName, setPatientName] = useState("");
  const [resetToken,  setResetToken]  = useState("");

  // Guard so session-restoration only runs once
  const sessionRestored = useRef(false);
  // Always-current ref so popstate handler (registered once) can read latest user
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  // ── navigate: updates view, sessionStorage, and browser history ──────────
  const navigate = useCallback((nextView) => {
    window.scrollTo({ top: 0, behavior: "instant" });
    sessionStorage.setItem("sc_view", nextView);
    // Push a real history entry so the browser back button works within the app
    window.history.pushState({ scView: nextView }, "", window.location.pathname);
    setView(nextView);
  }, []);

  // ── Seed the initial history entry so back never drops below the app ─────
  useEffect(() => {
    window.history.replaceState({ scView: "" }, "", window.location.pathname);
  }, []);

  // ── Browser back / forward ────────────────────────────────────────────────
  useEffect(() => {
    const onPop = (e) => {
      const targetView = e.state?.scView ?? "";
      if (userRef.current) {
        // Authenticated: block back/forward into non-dashboard views (login, landing)
        if (!RESTORABLE_VIEWS.has(targetView)) {
          const currentView = sessionStorage.getItem("sc_view") || "";
          window.history.pushState({ scView: currentView }, "", window.location.pathname);
          return;
        }
      } else {
        // Not authenticated: block back/forward into protected views (dashboard, onboarding)
        if (RESTORABLE_VIEWS.has(targetView)) {
          window.history.pushState({ scView: "" }, "", window.location.pathname);
          sessionStorage.setItem("sc_view", "");
          setView("");
          return;
        }
      }
      sessionStorage.setItem("sc_view", targetView);
      window.scrollTo({ top: 0, behavior: "instant" });
      setView(targetView);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // ── Handle ?reset_token= query param ─────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token  = params.get("reset_token");
    if (token) {
      setResetToken(token);
      navigate("reset-password");
    }
  }, [navigate]);

  const COMPLETE_STEPS = new Set(["clusters", "assessments", "packages", "review", "live"]);

  // ── Login success handler (fresh login & session restore fallback) ────────
  const handleLoginSuccess = useCallback(async (userData) => {
    if (userData?.role === "doctor") {
      setDoctorName(userData.name || "");
      try {
        const { data } = await api.get("/doctor/onboarding/me");
        navigate(COMPLETE_STEPS.has(data.onboarding_step) ? "doctor-dashboard" : "doctor-onboarding");
      } catch {
        navigate("doctor-onboarding");
      }
    } else if (userData?.role === "admin") {
      navigate("admin-dashboard");
    } else if (userData?.role === "patient") {
      setPatientName(userData.name || "");
      try {
        const { data } = await api.get("/patient/onboarding/status");
        navigate(data.onboarding_complete ? "user-dashboard" : "patient-onboarding");
      } catch {
        navigate("patient-onboarding");
      }
    } else {
      navigate("user-dashboard");
    }
  }, [navigate]);

  // ── Session restoration after AuthContext initializes ────────────────────
  useEffect(() => {
    if (!initialized) return;
    if (!user)        return;  // not logged in — stay on landing
    if (sessionRestored.current) return;
    sessionRestored.current = true;

    // If a dashboard view was saved, restore it immediately without extra API calls
    const saved = sessionStorage.getItem("sc_view");
    if (saved && RESTORABLE_VIEWS.has(saved)) {
      // Also sync browser history so back button works from the restored view
      window.history.replaceState({ scView: saved }, "", window.location.pathname);
      setView(saved);
      return;
    }

    // No saved view — determine correct destination from user role
    handleLoginSuccess(user);
  }, [initialized, user]); // intentionally excludes view and handleLoginSuccess

  // ── Loading screen while AuthContext is initialising ─────────────────────
  if (!initialized) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#EDF4F7" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 36, height: 36, border: "3px solid rgba(90,144,168,0.3)", borderTop: "3px solid #5A90A8", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 14px" }} />
          <p style={{ color: "#7AAABB", fontFamily: "Inter, sans-serif", fontSize: 14, margin: 0 }}>Loading…</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  const isAuthView = view !== "";

  return (
    <div className="App" data-testid="app-root">
      {!isAuthView && (
        <Navbar
          audience={audience}
          setAudience={setAudience}
          onLoginClick={() => navigate("login")}
        />
      )}

      {view === "login" ? (
        <main data-testid="login-page">
          <LoginPage
            onBack={() => navigate("")}
            audience={audience}
            onCreateAccount={() => navigate(audience === "doctors" ? "signup-doctor" : "signup")}
            onLoginSuccess={handleLoginSuccess}
            onForgotPassword={() => navigate("forgot-password")}
          />
        </main>
      ) : view === "forgot-password" ? (
        <main data-testid="forgot-password-page">
          <ForgotPasswordPage
            onBack={() => navigate("login")}
            onLoginClick={() => navigate("login")}
          />
        </main>
      ) : view === "reset-password" ? (
        <main data-testid="reset-password-page">
          <ResetPasswordPage
            token={resetToken}
            onLoginClick={() => { setResetToken(""); navigate("login"); }}
          />
        </main>
      ) : view === "signup" ? (
        <main data-testid="signup-page">
          <CreateAccountPage
            initialRole="patient"
            onBack={() => navigate("login")}
            onLoginClick={() => navigate("login")}
            onDoctorSignupSuccess={(name) => { setDoctorName(name); navigate("doctor-onboarding"); }}
            onPatientSignupSuccess={(name) => { setPatientName(name); navigate("patient-onboarding"); }}
          />
        </main>
      ) : view === "signup-doctor" ? (
        <main data-testid="signup-doctor-page">
          <CreateAccountPage
            initialRole="doctor"
            onBack={() => navigate("login")}
            onLoginClick={() => navigate("login")}
            onDoctorSignupSuccess={(name) => { setDoctorName(name); navigate("doctor-onboarding"); }}
            onPatientSignupSuccess={(name) => { setPatientName(name); navigate("patient-onboarding"); }}
          />
        </main>
      ) : view === "doctor-onboarding" ? (
        <main data-testid="doctor-onboarding-page">
          <DoctorOnboardingPage
            doctorName={doctorName}
            onFinish={() => navigate("doctor-dashboard")}
            onLogout={() => { navigate(""); setDoctorName(""); }}
          />
        </main>
      ) : view === "patient-onboarding" ? (
        <main data-testid="patient-onboarding-page">
          <PatientOnboardingPage
            patientName={patientName}
            onFinish={() => navigate("user-dashboard")}
            onLogout={() => { navigate(""); setPatientName(""); }}
          />
        </main>
      ) : view === "doctor-dashboard" ? (
        <main data-testid="doctor-dashboard-page">
          <DoctorDashboard
            onLogout={() => { navigate(""); setDoctorName(""); }}
          />
        </main>
      ) : view === "admin-dashboard" ? (
        <main data-testid="admin-dashboard-page">
          <AdminDashboard onLogout={() => navigate("")} />
        </main>
      ) : view === "user-dashboard" ? (
        <main data-testid="user-dashboard-page">
          <UserDashboard onLogout={() => navigate("")} />
        </main>
      ) : audience === "doctors" ? (
        <main data-testid="doctor-page">
          <Hero />
          <HeroMarquee />
          <FoundingStats />
          <QuietFrustration />
          <WhatSupercoreIs />
          <WhatThisChanges />
          <Clusters />
          <SpecialistsMosaic />
          <FirstNamed />
          <MovementStatement />
        </main>
      ) : (
        <main data-testid="patient-page">
          <PatientView />
        </main>
      )}

      {!isAuthView && <Footer audience={audience} />}
    </div>
  );
}

export default App;
